<template>
  <section class="gp-dashboard">
    <header class="gp-dashboard__hero">
      <div>
        <span class="gp-dashboard__eyebrow">Visão executiva</span>
        <h2>Panorama da operação GarantePay</h2>
        <p>Acompanhe carteira, conversão e pontos que precisam de atenção.</p>
      </div>
      <button type="button" class="gp-dashboard__pdf-button" :disabled="gerandoPdf" @click="gerarPdfDashboard">
        <v-icon size="18">mdi-file-pdf-box</v-icon>
        {{ gerandoPdf ? 'Gerando PDF...' : 'Gerar PDF' }}
      </button>
    </header>

    <div class="gp-dashboard__kpis">
      <article class="gp-kpi gp-kpi--primary">
        <div class="gp-kpi__icon"><v-icon>mdi-shield-check-outline</v-icon></div>
        <div class="gp-kpi__content">
          <span>Carteira vigente</span>
          <strong>{{ carteiraVigente.length }}</strong>
          <small>{{ moeda(valorCarteira) }} garantidos</small>
        </div>
        <router-link to="/ListaContratacoesVigentes" aria-label="Abrir contratações vigentes">
          <v-icon size="18">mdi-arrow-top-right</v-icon>
        </router-link>
      </article>

      <article class="gp-kpi gp-kpi--blue">
        <div class="gp-kpi__icon"><v-icon>mdi-progress-clock</v-icon></div>
        <div class="gp-kpi__content">
          <span>Em andamento</span>
          <strong>{{ emAndamento.length }}</strong>
          <small>{{ moeda(valorEmAndamento) }} em contratações</small>
        </div>
        <router-link to="/ListaContratacoes" aria-label="Abrir contratações em andamento">
          <v-icon size="18">mdi-arrow-top-right</v-icon>
        </router-link>
      </article>

      <article class="gp-kpi gp-kpi--warning">
        <div class="gp-kpi__icon"><v-icon>mdi-shield-alert-outline</v-icon></div>
        <div class="gp-kpi__content">
          <span>Vigentes em situação especial</span>
          <strong>{{ situacoesEspeciais.length }}</strong>
          <small :title="resumoSituacoesEspeciais">{{ resumoSituacoesEspeciais }}</small>
        </div>
        <router-link to="/ListaContratacoesVigentes" aria-label="Ver situações especiais">
          <v-icon size="18">mdi-arrow-top-right</v-icon>
        </router-link>
      </article>

      <article class="gp-kpi gp-kpi--success">
        <div class="gp-kpi__icon"><v-icon>mdi-clipboard-clock-outline</v-icon></div>
        <div class="gp-kpi__content">
          <span>Pendentes</span>
          <strong>{{ pedidosPendentes.length }}</strong>
          <small>Aguardando início do processo</small>
        </div>
        <router-link to="/NovaContratacao" aria-label="Abrir contratações pendentes">
          <v-icon size="18">mdi-arrow-top-right</v-icon>
        </router-link>
      </article>
    </div>

    <div class="gp-dashboard__summary">
      <article>
        <span>Garantidoras ativas</span>
        <strong>{{ garantidorasCount }}</strong>
        <v-icon>mdi-domain</v-icon>
      </article>
      <article>
        <span>Garantidoras inativas (bloqueadas)</span>
        <strong>{{ garantidorasBloqueadasCount }}</strong>
        <v-icon>mdi-domain-off</v-icon>
      </article>
      <article>
        <span>Imobiliárias cadastradas</span>
        <strong>{{ imobiliariasCount }}</strong>
        <v-icon>mdi-office-building-outline</v-icon>
      </article>
      <article>
        <span>Clientes cadastrados</span>
        <strong>{{ clientesCount }}</strong>
        <v-icon>mdi-account-group-outline</v-icon>
      </article>
    </div>

    <div class="gp-dashboard__grid gp-dashboard__grid--main">
      <article class="gp-panel gp-panel--ranking">
        <div class="gp-panel__header">
          <div>
            <span class="gp-panel__eyebrow">Carteira vigente</span>
            <h3>Desempenho por garantidora</h3>
          </div>
          <div class="gp-panel__actions">
            <router-link to="/Garantidoras">Ver cadastro <v-icon size="16">mdi-chevron-right</v-icon></router-link>
            <button type="button" :disabled="!desempenhoGarantidorasCompleto.length" @click="exportarDesempenhoExcel">
              <v-icon size="16">mdi-microsoft-excel</v-icon> Gerar Excel
            </button>
          </div>
        </div>

        <div v-if="rankingGarantidoras.length" class="gp-ranking-table">
          <div class="gp-ranking-table__head">
            <span>Garantidora</span><span>Vigentes</span><span>Em andamento</span><span>Valor garantido</span>
          </div>
          <div v-for="(item, index) in rankingGarantidoras" :key="item.nome" class="gp-ranking-table__row">
            <div><span class="gp-ranking-table__position">{{ index + 1 }}</span><strong>{{ item.nome }}</strong></div>
            <span>{{ item.vigentes }}</span>
            <span>{{ item.andamento }}</span>
            <strong>{{ moeda(item.valor) }}</strong>
          </div>
        </div>
        <div v-else class="gp-empty">
          <v-icon>mdi-domain-off</v-icon>
          <span>Nenhuma garantidora com contratação registrada.</span>
        </div>
      </article>

      <article class="gp-panel gp-panel--usage">
        <div class="gp-panel__header">
          <div>
            <span class="gp-panel__eyebrow">Mês atual</span>
            <h3>Uso da plataforma</h3>
          </div>
          <router-link to="/RelatorioUsoConsultasAssinaturas">Relatório <v-icon size="16">mdi-chevron-right</v-icon></router-link>
        </div>

        <div class="gp-usage-list">
          <div class="gp-usage-item">
            <div class="gp-usage-item__top">
              <span><v-icon size="18">mdi-account-search-outline</v-icon>Consultas de crédito</span>
              <strong>{{ consultasRealizadas }}<small v-if="totalConsultas"> / {{ totalConsultas }}</small></strong>
            </div>
            <div class="gp-usage-item__track"><span :style="{ width: usoPercentual(consultasRealizadas, totalConsultas) + '%' }"></span></div>
            <small>{{ usoLegenda(consultasRealizadas, totalConsultas) }}</small>
          </div>

          <div class="gp-usage-item gp-usage-item--signature">
            <div class="gp-usage-item__top">
              <span><v-icon size="18">mdi-file-sign</v-icon>Assinaturas digitais</span>
              <strong>{{ assinaturasRealizadas }}<small v-if="totalAssinaturas"> / {{ totalAssinaturas }}</small></strong>
            </div>
            <div class="gp-usage-item__track"><span :style="{ width: usoPercentual(assinaturasRealizadas, totalAssinaturas) + '%' }"></span></div>
            <small>{{ usoLegenda(assinaturasRealizadas, totalAssinaturas) }}</small>
          </div>
        </div>
      </article>
    </div>

    <div class="gp-dashboard__grid gp-dashboard__grid--charts">
      <article class="gp-panel gp-panel--chart">
        <div class="gp-panel__header">
          <div>
            <span class="gp-panel__eyebrow">Receita prevista</span>
            <h3>Evolução da carteira em receita</h3>
          </div>
          <span class="gp-panel__hint">Remuneração GarantePay por vencimento</span>
        </div>
        <div class="gp-chart-wrap">
          <Bar :data="receitaChart" :options="receitaBarOptions" />
        </div>
      </article>

      <article class="gp-panel gp-panel--chart gp-panel--status">
        <div class="gp-panel__header">
          <div>
            <span class="gp-panel__eyebrow">Distribuição geral</span>
            <h3>Situação da carteira</h3>
          </div>
        </div>
        <div class="gp-status-chart">
          <div class="gp-status-chart__canvas">
            <Doughnut :data="statusChart" :options="doughnutOptions" />
          </div>
          <div class="gp-status-chart__legend">
            <div v-for="item in resumoSituacoes" :key="item.label">
              <span :style="{ background: item.cor }"></span>
              <small>{{ item.label }}</small>
              <strong>{{ item.quantidade }}</strong>
            </div>
          </div>
        </div>
      </article>
    </div>

    <div class="gp-dashboard__grid gp-dashboard__grid--bottom">
      <article class="gp-panel gp-panel--inactive-portfolio">
        <div class="gp-panel__header">
          <div>
            <span class="gp-panel__eyebrow">Oportunidade de ativação</span>
            <h3>Garantidoras ativas sem contratações vigentes</h3>
          </div>
          <span class="gp-panel__count">{{ garantidorasSemVigentes.length }}</span>
        </div>

        <div v-if="garantidorasSemVigentes.length" class="gp-guarantor-list">
          <div v-for="item in garantidorasSemVigentes" :key="item.garId" class="gp-guarantor-item">
            <div class="gp-guarantor-item__icon"><v-icon size="18">mdi-domain</v-icon></div>
            <div>
              <strong>{{ item.nome || 'Garantidora sem nome' }}</strong>
              <small>{{ item.dataCadastro ? `Cadastrada em ${dataFormatada(item.dataCadastro)}` : 'Data de cadastro não informada' }}</small>
            </div>
            <span>Sem vigentes</span>
          </div>
        </div>
        <div v-else class="gp-empty gp-empty--compact">
          <v-icon>mdi-check-decagram-outline</v-icon>
          <span>Todas as garantidoras ativas possuem contratação vigente.</span>
        </div>
      </article>

      <article class="gp-panel gp-panel--claims">
        <div class="gp-panel__header">
          <div>
            <span class="gp-panel__eyebrow">Visão operacional</span>
            <h3>Sinistros por etapa</h3>
          </div>
          <span class="gp-panel__count gp-panel__count--blue">{{ totalPedidosSinistros }}</span>
        </div>

        <div v-if="sinistrosOrdenados.length" class="gp-claims-list">
          <div v-for="(item, index) in sinistrosOrdenados" :key="item.etapaId" class="gp-claims-item">
            <div class="gp-claims-item__top">
              <span><i :style="{ background: corSinistro(index) }"></i>{{ item.etapa || 'Etapa não informada' }}</span>
              <strong>{{ item.quantidadePedidos }}</strong>
            </div>
            <div class="gp-claims-item__track">
              <span :style="{ width: percentualSinistro(item.quantidadePedidos) + '%', background: corSinistro(index) }"></span>
            </div>
          </div>
        </div>
        <div v-else class="gp-empty gp-empty--compact">
          <v-icon>mdi-shield-check-outline</v-icon>
          <span>Nenhum sinistro registrado.</span>
        </div>
      </article>
    </div>

  </section>
</template>

<script>
import { Bar, Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip
} from 'chart.js'

ChartJS.register(ArcElement, BarElement, CategoryScale, Legend, LinearScale, Tooltip)

const STATUS = {
  vigente: 3,
  naoAprovado: 4,
  cancelado: 6,
  vigenteEspecial1: 16,
  vigenteEspecial2: 17
}

const STATUS_VIGENTES = [STATUS.vigente, STATUS.vigenteEspecial1, STATUS.vigenteEspecial2]
const STATUS_FORA_ANDAMENTO = [1, ...STATUS_VIGENTES, STATUS.naoAprovado, STATUS.cancelado, 10, 11, 12]

export default {
  name: 'GarantePayDashboard',
  components: { Bar, Doughnut },
  props: {
    pedidos: { type: Array, default: () => [] },
    garantidorasCount: { type: Number, default: 0 },
    garantidorasBloqueadasCount: { type: Number, default: 0 },
    imobiliariasCount: { type: Number, default: 0 },
    clientesCount: { type: Number, default: 0 },
    receitaCarteira: { type: Array, default: () => [] },
    garantidorasLista: { type: Array, default: () => [] },
    garantidorasSemVigentes: { type: Array, default: () => [] },
    sinistrosPorEtapa: { type: Array, default: () => [] },
    consultasRealizadas: { type: Number, default: 0 },
    assinaturasRealizadas: { type: Number, default: 0 },
    totalConsultas: { type: Number, default: 0 },
    totalAssinaturas: { type: Number, default: 0 }
  },
  data() {
    return {
      gerandoPdf: false,
      receitaBarOptions: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { left: 6, right: 22 } },
        interaction: { intersect: true, mode: 'nearest', axis: 'x' },
        plugins: {
          legend: { display: false },
          tooltip: {
            position: 'nearest',
            padding: 12,
            cornerRadius: 8,
            callbacks: {
              label: context => ` Receita prevista: ${Number(context.raw || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
            }
          }
        },
        scales: {
          x: { offset: true, grid: { display: false }, border: { display: false }, ticks: { color: '#768399' } },
          y: {
            beginAtZero: true,
            grid: { color: '#edf0f4' },
            border: { display: false },
            ticks: {
              color: '#768399',
              callback: value => `R$ ${Number(value || 0).toLocaleString('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })}`
            }
          }
        }
      },
      doughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        plugins: { legend: { display: false }, tooltip: { padding: 12, cornerRadius: 8 } }
      }
    }
  },
  computed: {
    pedidosNormalizados() {
      return (this.pedidos || []).map(item => ({
        ...item,
        pspId: Number(item.pspId || 0),
        pedValorTotalPedido: Number(item.pedValorTotalPedido || 0),
        pedCiclo: Number(item.pedCiclo || 1)
      }))
    },
    pedidosDaGarantidora() {
      return this.pedidosNormalizados
    },
    pedidosDoPeriodo() {
      const limite = new Date()
      limite.setMonth(limite.getMonth() - 12)
      return this.pedidosDaGarantidora.filter(item => {
        const data = this.dataValida(item.pedDataPedido)
        return data && data >= limite
      })
    },
    carteiraVigente() {
      return this.pedidosDaGarantidora.filter(item => STATUS_VIGENTES.includes(item.pspId))
    },
    valorCarteira() {
      return this.carteiraVigente.reduce((total, item) => total + item.pedValorTotalPedido, 0)
    },
    emAndamento() {
      return this.pedidosDaGarantidora.filter(item => !STATUS_FORA_ANDAMENTO.includes(item.pspId))
    },
    valorEmAndamento() {
      return this.emAndamento.reduce((total, item) => total + item.pedValorTotalPedido, 0)
    },
    pedidosPendentes() {
      return this.pedidosDaGarantidora.filter(item => item.pspId === 1)
    },
    novasNoPeriodo() {
      return this.pedidosDoPeriodo.length
    },
    situacoesEspeciais() {
      return this.pedidosDaGarantidora.filter(item => [STATUS.vigenteEspecial1, STATUS.vigenteEspecial2].includes(item.pspId))
    },
    resumoSituacoesEspeciais() {
      const status16 = this.situacoesEspeciais.filter(item => item.pspId === STATUS.vigenteEspecial1)
      const status17 = this.situacoesEspeciais.filter(item => item.pspId === STATUS.vigenteEspecial2)
      return `${this.nomeSituacao(status16, 'Status 16')}: ${status16.length} · ${this.nomeSituacao(status17, 'Status 17')}: ${status17.length}`
    },
    receitaNormalizada() {
      return (this.receitaCarteira || []).map(item => ({
        garId: Number(item.garId || 0),
        ano: Number(item.ano || 0),
        mes: Number(item.mes || 0),
        totalValor: Number(item.totalValor || 0)
      }))
    },
    receitaFiltrada() {
      return this.receitaNormalizada
    },
    mesesReceita() {
      const meses = []
      const hoje = new Date()
      const quantidade = 12
      for (let indice = quantidade - 1; indice >= 0; indice -= 1) {
        meses.push(new Date(hoje.getFullYear(), hoje.getMonth() - indice, 1))
      }
      return meses
    },
    receitaChart() {
      const labels = this.mesesReceita.map(data => data.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', ''))
      const valores = this.mesesReceita.map(data => this.receitaFiltrada
        .filter(item => item.ano === data.getFullYear() && item.mes === data.getMonth() + 1)
        .reduce((total, item) => total + item.totalValor, 0))
      return {
        labels,
        datasets: [{
          label: 'Receita prevista',
          data: valores,
          backgroundColor: '#174ea6',
          hoverBackgroundColor: '#ef7d00',
          borderRadius: 6,
          maxBarThickness: 30
        }]
      }
    },
    resumoSituacoes() {
      return [
        { label: 'Vigentes', quantidade: this.carteiraVigente.length, cor: '#16a36a' },
        { label: 'Em andamento', quantidade: this.emAndamento.length, cor: '#2f6fed' },
        { label: 'Não aprovadas', quantidade: this.pedidosDaGarantidora.filter(item => this.ehNegada(item)).length, cor: '#ef8b2c' },
        { label: 'Canceladas', quantidade: this.pedidosDaGarantidora.filter(item => this.ehCancelada(item)).length, cor: '#dc5260' }
      ]
    },
    statusChart() {
      return {
        labels: this.resumoSituacoes.map(item => item.label),
        datasets: [{
          data: this.resumoSituacoes.map(item => item.quantidade),
          backgroundColor: this.resumoSituacoes.map(item => item.cor),
          borderWidth: 0,
          hoverOffset: 5
        }]
      }
    },
    desempenhoGarantidorasCompleto() {
      const grupos = {}
      ;(this.garantidorasLista || []).forEach(item => {
        const id = Number(item.garId || 0)
        grupos[id] = {
          id,
          nome: item.nome || 'Não informada',
          situacao: item.bloqueada ? 'Bloqueada' : (item.ativa ? 'Ativa' : 'Inativa'),
          vigentes: 0,
          vigenteEspecial16: 0,
          vigenteEspecial17: 0,
          andamento: 0,
          pendentes: 0,
          naoAprovadas: 0,
          canceladas: 0,
          valor: 0
        }
      })
      this.pedidosDaGarantidora.forEach(item => {
        const id = Number(item.garId || 0)
        if (!grupos[id]) {
          grupos[id] = {
            id,
            nome: item.garNome || 'Não informada',
            situacao: 'Não informada',
            vigentes: 0,
            vigenteEspecial16: 0,
            vigenteEspecial17: 0,
            andamento: 0,
            pendentes: 0,
            naoAprovadas: 0,
            canceladas: 0,
            valor: 0
          }
        }
        const grupo = grupos[id]
        if (STATUS_VIGENTES.includes(item.pspId)) {
          grupo.vigentes += 1
          grupo.valor += item.pedValorTotalPedido
          if (item.pspId === STATUS.vigenteEspecial1) grupo.vigenteEspecial16 += 1
          if (item.pspId === STATUS.vigenteEspecial2) grupo.vigenteEspecial17 += 1
        } else if (!STATUS_FORA_ANDAMENTO.includes(item.pspId)) {
          grupo.andamento += 1
        }
        if (item.pspId === 1) grupo.pendentes += 1
        if (this.ehNegada(item)) grupo.naoAprovadas += 1
        if (this.ehCancelada(item)) grupo.canceladas += 1
      })
      return Object.values(grupos).sort((a, b) => b.valor - a.valor || b.vigentes - a.vigentes || a.nome.localeCompare(b.nome, 'pt-BR'))
    },
    rankingGarantidoras() {
      return this.desempenhoGarantidorasCompleto.slice(0, 6)
    },
    sinistrosOrdenados() {
      return (this.sinistrosPorEtapa || [])
        .map(item => ({
          ...item,
          etapaId: Number(item.etapaId || 0),
          quantidadePedidos: Number(item.quantidadePedidos || 0)
        }))
        .sort((a, b) => a.etapaId - b.etapaId)
    },
    totalPedidosSinistros() {
      return this.sinistrosOrdenados.reduce((total, item) => total + item.quantidadePedidos, 0)
    },
    maiorQuantidadeSinistros() {
      return Math.max(0, ...this.sinistrosOrdenados.map(item => item.quantidadePedidos))
    }
  },
  methods: {
    moeda(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    dataValida(valor) {
      if (!valor) return null
      const data = new Date(valor)
      return Number.isNaN(data.getTime()) ? null : data
    },
    ehCancelada(item) {
      return item.pspId === STATUS.cancelado || String(item.pedSituacaoPedido || '').toLowerCase().includes('cancel')
    },
    ehNegada(item) {
      if (this.ehCancelada(item)) return false
      const situacao = String(item.pedSituacaoPedido || '').toLowerCase()
      return item.pspId === STATUS.naoAprovado || situacao.includes('não aprovado') || situacao.includes('nao aprovado') || situacao.includes('negad')
    },
    nomeSituacao(itens, fallback) {
      const itemComNome = itens.find(item => item.pedSituacaoPedido)
      return itemComNome ? itemComNome.pedSituacaoPedido : fallback
    },
    usoPercentual(realizado, total) {
      if (!total) return realizado ? 100 : 0
      return Math.min(100, Math.round((realizado / total) * 100))
    },
    usoLegenda(realizado, total) {
      if (!total) return realizado ? 'Uso registrado no mês' : 'Nenhum uso registrado no mês'
      return `${this.usoPercentual(realizado, total)}% da capacidade utilizada`
    },
    dataFormatada(valor) {
      const data = this.dataValida(valor)
      return data ? data.toLocaleDateString('pt-BR') : ''
    },
    percentualSinistro(quantidade) {
      return this.maiorQuantidadeSinistros ? Math.max(4, Math.round((Number(quantidade || 0) / this.maiorQuantidadeSinistros) * 100)) : 0
    },
    corSinistro(index) {
      return ['#174ea6', '#ef7d00', '#16a36a', '#8b5cf6', '#dc5260', '#0891b2'][index % 6]
    },
    async exportarDesempenhoExcel() {
      if (!this.desempenhoGarantidorasCompleto.length) return
      const XLSX = await import('xlsx')
      const dados = this.desempenhoGarantidorasCompleto.map(item => ({
        'Código': item.id,
        'Garantidora': item.nome,
        'Situação cadastral': item.situacao,
        'Total vigentes': item.vigentes,
        'Vigentes status 16': item.vigenteEspecial16,
        'Vigentes status 17': item.vigenteEspecial17,
        'Em andamento': item.andamento,
        'Pendentes': item.pendentes,
        'Não aprovadas': item.naoAprovadas,
        'Canceladas': item.canceladas,
        'Valor garantido': item.valor
      }))
      const planilha = XLSX.utils.json_to_sheet(dados)
      planilha['!cols'] = [
        { wch: 10 }, { wch: 38 }, { wch: 20 }, { wch: 15 }, { wch: 19 }, { wch: 19 },
        { wch: 15 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 19 }
      ]
      for (let linha = 1; linha <= dados.length; linha += 1) {
        const celula = planilha[XLSX.utils.encode_cell({ r: linha, c: 10 })]
        if (celula) {
          celula.t = 'n'
          celula.z = 'R$ #,##0.00'
        }
      }
      const arquivo = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(arquivo, planilha, 'Desempenho')
      const dataArquivo = this.$moment ? this.$moment().format('YYYY-MM-DD-HHmm') : new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')
      XLSX.writeFile(arquivo, `desempenho-garantidoras-${dataArquivo}.xlsx`, { compression: true })
    },
    async gerarPdfDashboard() {
      if (this.gerandoPdf) return
      this.gerandoPdf = true
      try {
        const modulo = await import('jspdf')
        const jsPDF = modulo.default || modulo.jsPDF
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
        const larguraPagina = pdf.internal.pageSize.getWidth()
        const alturaPagina = pdf.internal.pageSize.getHeight()
        const margem = 12
        const larguraUtil = larguraPagina - (margem * 2)
        const navy = [8, 40, 88]
        const blue = [23, 78, 166]
        const orange = [239, 125, 0]
        const green = [22, 163, 106]
        const muted = [105, 119, 140]
        const border = [224, 229, 236]
        let primeiraPagina = true
        let y = 0
        let tituloPaginaAtual = ''

        const texto = (valor) => String(valor == null ? '' : valor)
        const numero = (valor) => Number(valor || 0).toLocaleString('pt-BR')
        const moedaPdf = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        const dataGeracao = new Date().toLocaleString('pt-BR')
        const truncar = (valor, limite = 42) => {
          const conteudo = texto(valor)
          return conteudo.length > limite ? `${conteudo.slice(0, limite - 1)}…` : conteudo
        }

        const novaPagina = (titulo) => {
          if (!primeiraPagina) pdf.addPage('a4', 'landscape')
          primeiraPagina = false
          tituloPaginaAtual = titulo
          pdf.setFillColor(...navy)
          pdf.rect(0, 0, larguraPagina, 24, 'F')
          pdf.setFillColor(...orange)
          pdf.rect(0, 24, larguraPagina, 1.5, 'F')
          pdf.setTextColor(255, 255, 255)
          pdf.setFont('helvetica', 'bold')
          pdf.setFontSize(16)
          pdf.text('GarantePay', margem, 10)
          pdf.setFontSize(11)
          pdf.text(titulo, margem, 18)
          pdf.setFont('helvetica', 'normal')
          pdf.setFontSize(8)
          pdf.text(`Gerado em ${dataGeracao}`, larguraPagina - margem, 17, { align: 'right' })
          pdf.setTextColor(...navy)
          y = 33
        }

        const garantirEspaco = (altura, titulo = tituloPaginaAtual) => {
          if (y + altura > alturaPagina - 14) novaPagina(titulo)
        }

        const tituloSecao = (titulo, complemento = '') => {
          garantirEspaco(12)
          pdf.setFont('helvetica', 'bold')
          pdf.setFontSize(11)
          pdf.setTextColor(...navy)
          pdf.text(titulo, margem, y)
          if (complemento) {
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(8)
            pdf.setTextColor(...muted)
            pdf.text(complemento, larguraPagina - margem, y, { align: 'right' })
          }
          y += 5
          pdf.setDrawColor(...border)
          pdf.line(margem, y, larguraPagina - margem, y)
          y += 5
        }

        const card = (x, topo, largura, rotulo, valor, detalhe, cor) => {
          pdf.setFillColor(249, 250, 252)
          pdf.setDrawColor(...border)
          pdf.roundedRect(x, topo, largura, 25, 2, 2, 'FD')
          pdf.setFillColor(...cor)
          pdf.roundedRect(x, topo, 2.2, 25, 1, 1, 'F')
          pdf.setFont('helvetica', 'bold')
          pdf.setFontSize(7)
          pdf.setTextColor(...muted)
          pdf.text(rotulo.toUpperCase(), x + 5, topo + 6)
          pdf.setFontSize(14)
          pdf.setTextColor(...navy)
          pdf.text(texto(valor), x + 5, topo + 14)
          pdf.setFont('helvetica', 'normal')
          pdf.setFontSize(7)
          pdf.setTextColor(...muted)
          pdf.text(truncar(detalhe, 38), x + 5, topo + 20)
        }

        const tabela = (titulo, colunas, linhas, larguras) => {
          tituloSecao(titulo, `${linhas.length} registro(s)`)
          const desenharCabecalho = () => {
            garantirEspaco(9, titulo)
            pdf.setFillColor(...navy)
            pdf.rect(margem, y, larguraUtil, 8, 'F')
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(6.8)
            pdf.setTextColor(255, 255, 255)
            let x = margem
            colunas.forEach((coluna, indice) => {
              pdf.text(coluna, x + 2, y + 5.2)
              x += larguras[indice]
            })
            y += 8
          }
          desenharCabecalho()
          if (!linhas.length) {
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(8)
            pdf.setTextColor(...muted)
            pdf.text('Nenhum registro encontrado.', margem + 2, y + 6)
            y += 10
            return
          }
          linhas.forEach((linha, indiceLinha) => {
            if (y + 7 > alturaPagina - 14) {
              novaPagina(titulo)
              desenharCabecalho()
            }
            if (indiceLinha % 2 === 0) {
              pdf.setFillColor(247, 249, 252)
              pdf.rect(margem, y, larguraUtil, 7, 'F')
            }
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(6.6)
            pdf.setTextColor(52, 67, 91)
            let x = margem
            linha.forEach((valor, indice) => {
              const alinhamento = indice === linha.length - 1 ? 'right' : 'left'
              const posicaoX = alinhamento === 'right' ? x + larguras[indice] - 2 : x + 2
              pdf.text(truncar(valor, indice === 1 ? 38 : 22), posicaoX, y + 4.7, { align: alinhamento })
              x += larguras[indice]
            })
            y += 7
          })
          y += 5
        }

        novaPagina('Dashboard executivo')
        const gap = 4
        const larguraCard = (larguraUtil - (gap * 3)) / 4
        card(margem, y, larguraCard, 'Carteira vigente', numero(this.carteiraVigente.length), `${moedaPdf(this.valorCarteira)} garantidos`, green)
        card(margem + larguraCard + gap, y, larguraCard, 'Em andamento', numero(this.emAndamento.length), `${moedaPdf(this.valorEmAndamento)} em contratações`, blue)
        card(margem + ((larguraCard + gap) * 2), y, larguraCard, 'Situação especial', numero(this.situacoesEspeciais.length), this.resumoSituacoesEspeciais, orange)
        card(margem + ((larguraCard + gap) * 3), y, larguraCard, 'Pendentes', numero(this.pedidosPendentes.length), 'Aguardando início do processo', [139, 92, 246])
        y += 30
        card(margem, y, larguraCard, 'Garantidoras ativas', numero(this.garantidorasCount), 'Acesso ativo à plataforma', green)
        card(margem + larguraCard + gap, y, larguraCard, 'Garantidoras bloqueadas', numero(this.garantidorasBloqueadasCount), 'Acesso encerrado pela data limite', orange)
        card(margem + ((larguraCard + gap) * 2), y, larguraCard, 'Imobiliárias', numero(this.imobiliariasCount), 'Cadastros na plataforma', blue)
        card(margem + ((larguraCard + gap) * 3), y, larguraCard, 'Clientes', numero(this.clientesCount), 'Cadastros na plataforma', navy)
        y += 33

        tituloSecao('Uso da plataforma e situação da carteira')
        pdf.setFontSize(8)
        pdf.setTextColor(...navy)
        pdf.setFont('helvetica', 'bold')
        pdf.text(`Consultas de crédito: ${numero(this.consultasRealizadas)}${this.totalConsultas ? ` de ${numero(this.totalConsultas)}` : ''}`, margem, y)
        pdf.text(`Assinaturas digitais: ${numero(this.assinaturasRealizadas)}${this.totalAssinaturas ? ` de ${numero(this.totalAssinaturas)}` : ''}`, margem + 70, y)
        this.resumoSituacoes.forEach((item, indice) => {
          pdf.setTextColor(...muted)
          pdf.setFont('helvetica', 'normal')
          pdf.text(`${item.label}:`, margem + 145 + (indice * 30), y)
          pdf.setTextColor(...navy)
          pdf.setFont('helvetica', 'bold')
          pdf.text(numero(item.quantidade), margem + 171 + (indice * 30), y, { align: 'right' })
        })
        y += 11

        tituloSecao('Evolução da carteira em receita', 'Receita prevista por vencimento — últimos 12 meses')
        const meses = this.mesesReceita.map(data => ({
          label: data.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', ''),
          valor: this.receitaFiltrada
            .filter(item => item.ano === data.getFullYear() && item.mes === data.getMonth() + 1)
            .reduce((total, item) => total + item.totalValor, 0)
        }))
        const larguraMes = larguraUtil / 6
        meses.forEach((item, indice) => {
          const linha = Math.floor(indice / 6)
          const coluna = indice % 6
          const x = margem + (coluna * larguraMes)
          const topo = y + (linha * 17)
          pdf.setDrawColor(...border)
          pdf.setFillColor(249, 250, 252)
          pdf.roundedRect(x + 1, topo, larguraMes - 2, 14, 1.5, 1.5, 'FD')
          pdf.setFont('helvetica', 'normal')
          pdf.setFontSize(7)
          pdf.setTextColor(...muted)
          pdf.text(item.label.toUpperCase(), x + 4, topo + 5)
          pdf.setFont('helvetica', 'bold')
          pdf.setFontSize(8)
          pdf.setTextColor(...navy)
          pdf.text(moedaPdf(item.valor), x + 4, topo + 10.5)
        })
        y += 39

        novaPagina('Desempenho completo por garantidora')
        tabela(
          'Desempenho por garantidora',
          ['Cód.', 'Garantidora', 'Situação', 'Vig.', 'St. 16', 'St. 17', 'Andam.', 'Pend.', 'Neg.', 'Canc.', 'Valor garantido'],
          this.desempenhoGarantidorasCompleto.map(item => [
            item.id,
            item.nome,
            item.situacao,
            item.vigentes,
            item.vigenteEspecial16,
            item.vigenteEspecial17,
            item.andamento,
            item.pendentes,
            item.naoAprovadas,
            item.canceladas,
            moedaPdf(item.valor)
          ]),
          [14, 53, 25, 16, 16, 16, 19, 16, 16, 16, 46]
        )

        novaPagina('Indicadores operacionais')
        tabela(
          'Garantidoras ativas sem contratações vigentes',
          ['Código', 'Garantidora', 'Data de cadastro'],
          this.garantidorasSemVigentes.map(item => [item.garId, item.nome, item.dataCadastro ? this.dataFormatada(item.dataCadastro) : 'Não informada']),
          [25, 175, 73]
        )
        tabela(
          'Sinistros por etapa',
          ['Código da etapa', 'Etapa atual', 'Quantidade de ped_ids'],
          this.sinistrosOrdenados.map(item => [item.etapaId, item.etapa || 'Não informada', item.quantidadePedidos]),
          [35, 175, 63]
        )

        const totalPaginas = pdf.getNumberOfPages()
        for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
          pdf.setPage(pagina)
          pdf.setDrawColor(...border)
          pdf.line(margem, alturaPagina - 10, larguraPagina - margem, alturaPagina - 10)
          pdf.setFont('helvetica', 'normal')
          pdf.setFontSize(7)
          pdf.setTextColor(...muted)
          pdf.text('Relatório gerencial GarantePay', margem, alturaPagina - 5.5)
          pdf.text(`Página ${pagina} de ${totalPaginas}`, larguraPagina - margem, alturaPagina - 5.5, { align: 'right' })
        }

        const dataArquivo = this.$moment ? this.$moment().format('YYYY-MM-DD-HHmm') : new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')
        pdf.save(`dashboard-garantepay-${dataArquivo}.pdf`)
      } catch (erro) {
        console.error('Erro ao gerar PDF do dashboard:', erro)
        if (this.$mensagemErro) this.$mensagemErro('Não foi possível gerar o PDF do dashboard.')
      } finally {
        this.gerandoPdf = false
      }
    }
  }
}
</script>

<style scoped>
.gp-dashboard {
  --gp-navy: #082858;
  --gp-blue: #174ea6;
  --gp-orange: #ef7d00;
  --gp-border: #e5e9ef;
  --gp-muted: #718096;
  color: #14213d;
  padding-bottom: 24px;
}

.gp-dashboard__hero {
  align-items: flex-end;
  background: linear-gradient(125deg, #082858 0%, #0e3c78 67%, #174ea6 100%);
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(8, 40, 88, .16);
  color: #fff;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 18px;
  overflow: hidden;
  padding: 24px 26px;
  position: relative;
}

.gp-dashboard__hero::after {
  background: rgba(255, 255, 255, .07);
  border-radius: 999px;
  content: '';
  height: 220px;
  position: absolute;
  right: -50px;
  top: -100px;
  width: 220px;
}

.gp-dashboard__eyebrow,
.gp-panel__eyebrow {
  display: block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .09em;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.gp-dashboard__hero h2 { font-size: 22px; font-weight: 800; margin: 0 0 4px; }
.gp-dashboard__hero p { color: rgba(255, 255, 255, .72); font-size: 12px; margin: 0; }
.gp-dashboard__pdf-button { align-items: center; background: #fff; border: 0; border-radius: 9px; color: var(--gp-navy); display: flex; font-size: 11px; font-weight: 800; gap: 6px; padding: 10px 14px; position: relative; z-index: 1; }
.gp-dashboard__pdf-button .v-icon { color: #dc3545; }
.gp-dashboard__pdf-button:hover { background: #f7f9fc; }
.gp-dashboard__pdf-button:disabled { cursor: wait; opacity: .7; }

.gp-dashboard__kpis { display: grid; gap: 12px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 12px; }
.gp-kpi {
  align-items: center;
  background: #fff;
  border: 1px solid var(--gp-border);
  border-radius: 13px;
  box-shadow: 0 4px 14px rgba(20, 33, 61, .045);
  display: grid;
  gap: 12px;
  grid-template-columns: auto 1fr auto;
  min-height: 112px;
  padding: 17px;
  position: relative;
}
.gp-kpi::before { background: #16a36a; border-radius: 13px 0 0 13px; content: ''; inset: 0 auto 0 0; position: absolute; width: 3px; }
.gp-kpi--blue::before { background: #2f6fed; }
.gp-kpi--warning::before { background: #ef8b2c; }
.gp-kpi--success::before { background: #8b5cf6; }
.gp-kpi__icon { align-items: center; background: #e9f8f1; border-radius: 11px; color: #14835a; display: flex; height: 42px; justify-content: center; width: 42px; }
.gp-kpi--blue .gp-kpi__icon { background: #edf3ff; color: #2f6fed; }
.gp-kpi--warning .gp-kpi__icon { background: #fff4e7; color: #df7200; }
.gp-kpi--success .gp-kpi__icon { background: #f3efff; color: #7c4ce0; }
.gp-kpi__content { display: flex; flex-direction: column; min-width: 0; }
.gp-kpi__content > span { color: var(--gp-muted); font-size: 10px; font-weight: 700; text-transform: uppercase; }
.gp-kpi__content strong { color: var(--gp-navy); font-size: 25px; line-height: 1.12; margin: 3px 0; }
.gp-kpi__content small { color: #748196; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gp-kpi > a { align-items: center; border: 1px solid #e4e9f1; border-radius: 7px; color: #55708f; display: flex; height: 29px; justify-content: center; width: 29px; }
.gp-kpi__trend { align-items: center; color: #7657bd; display: flex; font-size: 9px; gap: 3px; white-space: nowrap; }

.gp-dashboard__summary { display: grid; gap: 10px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 12px; }
.gp-dashboard__summary article { align-items: center; background: #f8fafc; border: 1px solid #e8ecf1; border-radius: 10px; display: grid; grid-template-columns: 1fr auto; padding: 12px 14px; }
.gp-dashboard__summary span { color: var(--gp-muted); font-size: 10px; grid-column: 1; }
.gp-dashboard__summary strong { color: var(--gp-navy); font-size: 17px; grid-column: 1; }
.gp-dashboard__summary .v-icon { color: #9aa7b8; grid-column: 2; grid-row: 1 / 3; }

.gp-dashboard__grid { display: grid; gap: 12px; margin-bottom: 12px; }
.gp-dashboard__grid--main { grid-template-columns: minmax(0, 1.35fr) minmax(320px, .9fr); }
.gp-dashboard__grid--charts { grid-template-columns: minmax(0, 1.45fr) minmax(320px, .75fr); }
.gp-dashboard__grid--bottom { grid-template-columns: minmax(0, 1.4fr) minmax(300px, .7fr); }
.gp-panel { background: #fff; border: 1px solid var(--gp-border); border-radius: 13px; box-shadow: 0 4px 14px rgba(20, 33, 61, .035); min-width: 0; padding: 18px; }
.gp-panel__header { align-items: center; display: flex; justify-content: space-between; margin-bottom: 16px; }
.gp-panel__eyebrow { color: #8a96a8; }
.gp-panel__header h3 { color: var(--gp-navy); font-size: 14px; font-weight: 800; margin: 0; }
.gp-panel__header a { align-items: center; color: #1767da; display: flex; font-size: 10px; font-weight: 700; text-decoration: none; }
.gp-panel__actions { align-items: center; display: flex; gap: 8px; }
.gp-panel__actions button { align-items: center; background: #eef8f1; border: 1px solid #cae8d3; border-radius: 7px; color: #168447; display: flex; font-size: 10px; font-weight: 800; gap: 4px; padding: 7px 9px; }
.gp-panel__actions button:hover { background: #e1f3e7; }
.gp-panel__actions button:disabled { cursor: not-allowed; opacity: .5; }
.gp-panel__count { align-items: center; background: #fff1e4; border-radius: 999px; color: #d96e00; display: flex; font-size: 11px; font-weight: 800; height: 27px; justify-content: center; min-width: 27px; padding: 0 8px; }
.gp-panel__count--blue { background: #edf3ff; color: #174ea6; }
.gp-panel__hint { color: #929caf; font-size: 9px; }

.gp-pipeline { display: flex; flex-direction: column; gap: 12px; }
.gp-pipeline__label { align-items: center; display: grid; font-size: 10px; grid-template-columns: auto 1fr auto; margin-bottom: 5px; }
.gp-pipeline__label strong { color: var(--gp-navy); }
.gp-pipeline__dot { border-radius: 50%; height: 7px; margin-right: 7px; width: 7px; }
.gp-pipeline__track { background: #edf0f5; border-radius: 999px; height: 6px; overflow: hidden; }
.gp-pipeline__track span { border-radius: inherit; display: block; height: 100%; transition: width .25s ease; }

.gp-attention-list { display: flex; flex-direction: column; gap: 6px; }
.gp-attention-item { align-items: center; border-bottom: 1px solid #edf0f4; display: grid; gap: 10px; grid-template-columns: auto 1fr auto; padding: 7px 0 10px; }
.gp-attention-item:last-child { border-bottom: 0; padding-bottom: 0; }
.gp-attention-item__icon { align-items: center; background: #fff2e6; border-radius: 8px; color: #df7200; display: flex; height: 33px; justify-content: center; width: 33px; }
.gp-attention-item__icon.is-purple { background: #f2efff; color: #805ad5; }
.gp-attention-item__icon.is-amber { background: #fff7df; color: #c88700; }
.gp-attention-item > div { display: flex; flex-direction: column; min-width: 0; }
.gp-attention-item strong { color: var(--gp-navy); font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gp-attention-item span, .gp-attention-item small { color: #7d899b; font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gp-attention-item__code { background: #f4f6f9; border-radius: 5px; color: #627087 !important; padding: 4px 6px; }

.gp-chart-wrap { height: 245px; }
.gp-status-chart { align-items: center; display: grid; gap: 18px; grid-template-columns: minmax(135px, .9fr) minmax(120px, 1fr); min-height: 245px; }
.gp-status-chart__canvas { height: 180px; min-width: 0; }
.gp-status-chart__legend { display: flex; flex-direction: column; gap: 10px; }
.gp-status-chart__legend > div { align-items: center; display: grid; gap: 7px; grid-template-columns: auto 1fr auto; }
.gp-status-chart__legend span { border-radius: 50%; height: 7px; width: 7px; }
.gp-status-chart__legend small { color: #6d798c; font-size: 9px; }
.gp-status-chart__legend strong { color: var(--gp-navy); font-size: 11px; }

.gp-ranking-table { overflow-x: auto; }
.gp-ranking-table__head, .gp-ranking-table__row { align-items: center; display: grid; gap: 12px; grid-template-columns: minmax(190px, 1.5fr) 70px 90px 115px; min-width: 540px; }
.gp-ranking-table__head { border-bottom: 1px solid #e8ecf1; color: #8b96a8; font-size: 8px; font-weight: 800; padding: 0 6px 8px; text-transform: uppercase; }
.gp-ranking-table__row { border-bottom: 1px solid #f0f2f5; font-size: 10px; padding: 10px 6px; }
.gp-ranking-table__row:last-child { border-bottom: 0; }
.gp-ranking-table__row > div { align-items: center; display: flex; gap: 8px; min-width: 0; }
.gp-ranking-table__row > div strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gp-ranking-table__row > span { color: #637086; }
.gp-ranking-table__position { align-items: center; background: #edf3ff; border-radius: 6px; color: #2f6fed !important; display: flex; flex: 0 0 auto; font-size: 9px; font-weight: 800; height: 23px; justify-content: center; width: 23px; }

.gp-usage-list { display: flex; flex-direction: column; gap: 22px; padding-top: 5px; }
.gp-usage-item__top { align-items: center; display: flex; justify-content: space-between; margin-bottom: 8px; }
.gp-usage-item__top > span { align-items: center; color: #4e5d73; display: flex; font-size: 10px; font-weight: 700; gap: 7px; }
.gp-usage-item__top .v-icon { color: #2f6fed; }
.gp-usage-item__top strong { color: var(--gp-navy); font-size: 14px; }
.gp-usage-item__top strong small { color: #8894a6; font-size: 9px; }
.gp-usage-item__track { background: #edf0f4; border-radius: 999px; height: 7px; overflow: hidden; }
.gp-usage-item__track span { background: linear-gradient(90deg, #174ea6, #4f83ef); border-radius: inherit; display: block; height: 100%; }
.gp-usage-item > small { color: #8b96a8; display: block; font-size: 9px; margin-top: 6px; }
.gp-usage-item--signature .gp-usage-item__top .v-icon { color: #ef7d00; }
.gp-usage-item--signature .gp-usage-item__track span { background: linear-gradient(90deg, #ef7d00, #f5a13d); }

.gp-guarantor-list, .gp-claims-list { display: flex; flex-direction: column; max-height: 310px; overflow-y: auto; padding-right: 5px; }
.gp-guarantor-list { gap: 4px; }
.gp-guarantor-item { align-items: center; border-bottom: 1px solid #edf0f4; display: grid; gap: 10px; grid-template-columns: auto 1fr auto; padding: 9px 2px; }
.gp-guarantor-item:last-child { border-bottom: 0; }
.gp-guarantor-item__icon { align-items: center; background: #fff4e7; border-radius: 8px; color: #df7200; display: flex; height: 34px; justify-content: center; width: 34px; }
.gp-guarantor-item > div:nth-child(2) { display: flex; flex-direction: column; min-width: 0; }
.gp-guarantor-item strong { color: var(--gp-navy); font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gp-guarantor-item small { color: #8995a7; font-size: 9px; }
.gp-guarantor-item > span { background: #fff4e7; border-radius: 999px; color: #d96e00; font-size: 8px; font-weight: 800; padding: 5px 8px; white-space: nowrap; }
.gp-claims-list { gap: 16px; }
.gp-claims-item__top { align-items: center; display: flex; justify-content: space-between; margin-bottom: 6px; }
.gp-claims-item__top > span { align-items: center; color: #526077; display: flex; font-size: 10px; font-weight: 700; gap: 8px; }
.gp-claims-item__top i { border-radius: 50%; display: inline-block; height: 8px; width: 8px; }
.gp-claims-item__top strong { color: var(--gp-navy); font-size: 12px; }
.gp-claims-item__track { background: #edf0f4; border-radius: 999px; height: 7px; overflow: hidden; }
.gp-claims-item__track span { border-radius: inherit; display: block; height: 100%; transition: width .25s ease; }

.gp-empty { align-items: center; color: #8a96a8; display: flex; flex-direction: column; font-size: 10px; gap: 8px; justify-content: center; min-height: 150px; text-align: center; }
.gp-empty .v-icon { color: #b0bac8; }
.gp-empty--compact { min-height: 120px; }

@media (max-width: 1199px) {
  .gp-dashboard__kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gp-dashboard__grid--main, .gp-dashboard__grid--charts, .gp-dashboard__grid--bottom { grid-template-columns: 1fr; }
}

@media (max-width: 767px) {
  .gp-dashboard__hero { align-items: stretch; flex-direction: column; }
  .gp-dashboard__pdf-button { justify-content: center; width: 100%; }
  .gp-dashboard__kpis, .gp-dashboard__summary { grid-template-columns: 1fr; }
  .gp-status-chart { grid-template-columns: 1fr; }
  .gp-status-chart__canvas { height: 200px; }
}
</style>
